/**
 * Module preloader utility to proactively load critical Vue components
 * This helps prevent "Failed to fetch dynamically imported module" errors
 * by warming up the module cache with commonly accessed routes.
 */

interface PreloadableRoute {
    path: string;
    moduleLoader: () => Promise<any>;
    priority: 'high' | 'medium' | 'low';
}

class ModulePreloader {
    private preloadedModules = new Set<string>();
    private preloadingPromises = new Map<string, Promise<any>>();

    /**
     * Define routes that should be preloaded
     * Note: Only main views are preloaded to avoid circular dependency issues.
     * Child components are loaded automatically by Vite when needed.
     */
    private readonly PRELOADABLE_ROUTES: PreloadableRoute[] = [
        // Inbox views - highest priority since it's the default landing page
        {
            path: '/inbox',
            moduleLoader: () => import('@/modules/inbox/views/InboxView.vue'),
            priority: 'high'
        },
        {
            path: '/inbox/case',
            moduleLoader: () => import('@/modules/inbox/views/InboxCase.vue'),
            priority: 'high'
        },
        {
            path: '/inbox/task',
            moduleLoader: () => import('@/modules/inbox/views/InboxTask.vue'),
            priority: 'high'
        },
        // Knowledge - also commonly accessed
        {
            path: '/knowledge',
            moduleLoader: () => import('@/modules/knowledge/views/KnowledgeView.vue'),
            priority: 'high'
        },
        // Other routes - medium priority
        {
            path: '/customers',
            moduleLoader: () => import('@/modules/customer/views/CustomersView.vue'),
            priority: 'medium'
        },
        {
            path: '/settings',
            moduleLoader: () => import('@/modules/settings/views/SettingsView.vue'),
            priority: 'medium'
        },
        {
            path: '/journeys',
            moduleLoader: () => import('@/views/JourneysView.vue'),
            priority: 'medium'
        }
    ];

    /**
     * Preload a specific module
     */
    async preloadModule(route: PreloadableRoute): Promise<void> {
        if (this.preloadedModules.has(route.path)) {
            return; // Already preloaded
        }

        if (this.preloadingPromises.has(route.path)) {
            return this.preloadingPromises.get(route.path); // Already preloading
        }

        
        const preloadPromise = route.moduleLoader()
            .then(() => {
                this.preloadedModules.add(route.path);
                this.preloadingPromises.delete(route.path);
            })
            .catch((error) => {
                console.warn('⚠️ Failed to preload ' + route.path + ':', error);
                this.preloadingPromises.delete(route.path);
                // Don't rethrow - preloading failures shouldn't break the app
            });

        this.preloadingPromises.set(route.path, preloadPromise);
        return preloadPromise;
    }

    /**
     * Preload modules based on priority
     */
    async preloadByPriority(priority: 'high' | 'medium' | 'low'): Promise<void> {
        const routesToPreload = this.PRELOADABLE_ROUTES.filter(route => route.priority === priority);
        
        const preloadPromises = routesToPreload.map(route => 
            this.preloadModule(route).catch(() => {
                // Ignore individual failures for batch operations
            })
        );

        await Promise.allSettled(preloadPromises);
    }

    /**
     * Preload all high-priority modules
     */
    async preloadCriticalModules(): Promise<void> {
        await this.preloadByPriority('high');
    }

    /**
     * Aggressively preload all inbox-related modules immediately
     * This is called after successful login to ensure inbox loads instantly
     */
    async preloadInboxModulesAggressively(): Promise<void> {
        const inboxRoutes = this.PRELOADABLE_ROUTES.filter(route => 
            route.path.startsWith('/inbox')
        );
        
        // Preload all inbox modules in parallel for maximum speed
        const preloadPromises = inboxRoutes.map(route => 
            this.preloadModule(route).catch((error) => {
                console.warn('⚠️ Failed to preload inbox module ' + route.path + ':', error);
                // Don't throw - continue preloading other modules
            })
        );

        await Promise.allSettled(preloadPromises);
    }

    /**
     * Preload modules when the browser is idle
     */
    preloadOnIdle(): void {
        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(() => {
                this.preloadByPriority('medium');
            });
        } else {
            // Fallback for browsers without requestIdleCallback
            setTimeout(() => {
                this.preloadByPriority('medium');
            }, 2000);
        }
    }

    /**
     * Check if a module is already preloaded
     */
    isPreloaded(path: string): boolean {
        return this.preloadedModules.has(path);
    }

    /**
     * Get preloading status
     */
    getStatus() {
        return {
            preloaded: Array.from(this.preloadedModules),
            preloading: Array.from(this.preloadingPromises.keys())
        };
    }
}

// Export singleton instance
export const modulePreloader = new ModulePreloader();

/**
 * Initialize module preloading - call this in your main app setup
 */
export function initializeModulePreloading(): void {
    
    // Preload critical modules immediately
    modulePreloader.preloadCriticalModules();

    // Preload medium priority modules when idle
    modulePreloader.preloadOnIdle();
}

/**
 * Preload inbox modules aggressively after login
 * This ensures the inbox loads instantly when the user navigates to it
 */
export async function preloadInboxAfterLogin(): Promise<void> {
    await modulePreloader.preloadInboxModulesAggressively();
}

